import React, { useState, useContext, useEffect } from "react";
import Axios from "axios";
import AxiosInstance from "../../AxiosInstance";
import { useTranslation } from "react-i18next";
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    Typography,
    Chip,
    Stack,
    Snackbar,
    Alert,
    CircularProgress
} from "@mui/material";
import StateContext from "../../Contexts/StateContext";

function TwoFASetup() {
    const { t } = useTranslation();
    const { config, environment } = require("../../index");
    const GlobalState = useContext(StateContext);

    const [open, setOpen] = useState(false);
    const [qrCode, setQrCode] = useState("");
    const [otpSecret, setOtpSecret] = useState("");
    const [token, setToken] = useState("");
    const [enabled, setEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [requestInProgress, setRequestInProgress] = useState(false);

    const [snackOpen, setSnackOpen] = useState(false);
    const [snackMsg, setSnackMsg] = useState("");
    const [snackSeverity, setSnackSeverity] = useState("success");

    const authHeaders = {
        headers: { Authorization: `Bearer ${GlobalState.jwtAccessToken}` }
    };

    // Fetch current 2FA status
    useEffect(() => {
        if (!GlobalState.jwtAccessToken) return;

        const source = Axios.CancelToken.source();

        const fetchStatus = async () => {
            try {
                const res = await AxiosInstance.get(config[environment].twoFAStatusEndpoint, {
                    cancelToken: source.token
                });
                setEnabled(res.data.is_2fa_enabled);
            } catch (err) {
                console.error("[TwoFA] Fetch status failed:", err);
                setSnackMsg(t("Administration.TwoFASetup.FetchStatusFailed"));
                setSnackSeverity("error");
                setSnackOpen(true);
            } finally {
                setLoading(false);
            }
        };

        fetchStatus();
        return () => source.cancel();
    }, [GlobalState.jwtAccessToken, config, environment]);

    // Open enable 2FA dialog
    const openDialog = async () => {
        setToken("");
        setSnackOpen(false);
        setRequestInProgress(true);

        try {
            const res = await AxiosInstance.post(config[environment].enable2FAEndpoint, {});
            setQrCode(res.data.qr_code);
            setOtpSecret(res.data.secret);
            setOpen(true);
        } catch (err) {
            console.error("[TwoFA] Enable dialog failed:", err);
            setSnackMsg(t("Administration.TwoFASetup.GenerateFailed"));
            setSnackSeverity("error");
            setSnackOpen(true);
        } finally {
            setRequestInProgress(false);
        }
    };

    // Confirm OTP code
    const confirm2FA = async () => {
        if (!token || token.trim().length !== 6) {
            setSnackMsg(t("Administration.TwoFASetup.TokenPlaceholder"));
            setSnackSeverity("warning");
            setSnackOpen(true);
            return;
        }

        setRequestInProgress(true);
        try {
            const res = await AxiosInstance.post(config[environment].confirm2FAEndpoint, { token: token.trim() });
            setEnabled(true);
            setOpen(false);
            setSnackMsg(res.data.detail || t("Administration.TwoFASetup.ConfirmSuccess"));
            setSnackSeverity("success");
            setSnackOpen(true);
        } catch (err) {
            console.error("[TwoFA] Confirm failed:", err);
            setSnackMsg(err.response?.data?.detail || "Invalid OTP token. Try again.");
            setSnackOpen(true);
            setSnackSeverity("error");
        } finally {
            setRequestInProgress(false);
        }
    };

    // Disable 2FA
    const disable2FA = async () => {
        setRequestInProgress(true);
        try {
            await AxiosInstance.post(config[environment].disable2FAEndpoint, {});
            setEnabled(false);
            setSnackMsg(t("Administration.TwoFASetup.DisableSuccess"));
            setSnackSeverity("success");
            setSnackOpen(true);
        } catch (err) {
            console.error("[TwoFA] Disable failed:", err);
            setSnackMsg(t("Administration.TwoFASetup.DisableFailed"));
            setSnackSeverity("error");
            setSnackOpen(true);
        } finally {
            setRequestInProgress(false);
        }
    };

    if (loading) return <CircularProgress sx={{ mt: 5 }} />;

    return (
        <div style={{ textAlign: "center" }}>
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 2 }}>
                {enabled ? (
                    <>
                        <Chip label={t("Administration.TwoFASetup.Enabled")} color="success" sx={{ fontSize: "1rem", p: 1 }} />
                        <Button variant="outlined" color="error" onClick={disable2FA} disabled={requestInProgress}>
                            {t("Administration.TwoFASetup.Disabled")}
                        </Button>
                    </>
                ) : (
                    <Button variant="contained" color="primary" onClick={openDialog} disabled={requestInProgress}>
                        {t("Administration.TwoFASetup.EnableButton")}
                    </Button>
                )}
            </Stack>

            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>{t("Administration.TwoFASetup.DialogTitle")}</DialogTitle>
                <DialogContent>
                    <Typography>{t("Administration.TwoFASetup.ScanQR")}</Typography>
                    {qrCode && (
                        <img src={`data:image/png;base64,${qrCode}`} alt="QR Code" style={{ marginTop: "1rem" }} />
                    )}
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        {t("Administration.TwoFASetup.SecretLabel")} <b>{otpSecret}</b>
                    </Typography>
                    <TextField
                        label={t("Administration.TwoFASetup.TokenPlaceholder")}
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={requestInProgress}>
                        {t("Global.Cancel") || "Mégse"}
                    </Button>
                    <Button variant="contained" onClick={confirm2FA} disabled={requestInProgress}>
                        {t("Administration.TwoFASetup.Confirm")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackOpen}
                autoHideDuration={4000}
                onClose={() => setSnackOpen(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={() => setSnackOpen(false)} severity={snackSeverity} sx={{ width: "100%" }}>
                    {snackMsg}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default TwoFASetup;
